import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accessibility, Save } from "lucide-react";
import { toast } from "sonner";

const emptyProfile = {
  is_paralympic: false,
  discipline: null,
  functional_classification: null,
  classification_status: null,
  classification_date: "",
  classifying_body: "",
  expiry_date: "",
  notes: "",
  document_url: "",
};

export default function ParalympicProfileSection({ athleteId, canEdit = true }) {
  const [profile, setProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!athleteId) return;
    api.get(`/athletes/${athleteId}/paralympic-profile`).then((r) => {
      setProfile({ ...emptyProfile, ...r.data });
    }).catch(() => setProfile(emptyProfile));
  }, [athleteId]);

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...profile, athlete_id: athleteId };
      if (payload.discipline === "non_vedenti") payload.functional_classification = null;
      const { data } = await api.put(`/athletes/${athleteId}/paralympic-profile`, payload);
      setProfile({ ...emptyProfile, ...data });
      toast.success("Profilo paralimpico salvato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setLoading(false); }
  };

  const showClassification = profile.discipline === "carrozzina";

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5 max-w-3xl space-y-4" data-testid="paralympic-section">
      <div className="flex items-center gap-2">
        <Accessibility className="w-5 h-5 text-[#0052FF]" />
        <h3 className="font-heading font-semibold">Settore e classificazione paralimpica</h3>
      </div>

      <div className="flex items-center gap-3">
        <input
          id="is-paralympic"
          type="checkbox"
          data-testid="paralympic-toggle"
          checked={!!profile.is_paralympic}
          disabled={!canEdit}
          onChange={(e) => setProfile({ ...profile, is_paralympic: e.target.checked, discipline: e.target.checked ? profile.discipline : null, functional_classification: e.target.checked ? profile.functional_classification : null })}
          className="w-4 h-4"
        />
        <Label htmlFor="is-paralympic" className="cursor-pointer">Atleta paralimpico</Label>
        <span className="text-xs text-zinc-500">(mutuamente esclusivo rispetto al settore olimpico)</span>
      </div>

      {profile.is_paralympic && (
        <div className="grid lg:grid-cols-2 gap-3 pt-2 border-t border-zinc-100">
          <div>
            <Label>Disciplina</Label>
            <Select value={profile.discipline || ""} onValueChange={(v) => setProfile({ ...profile, discipline: v, functional_classification: v === "non_vedenti" ? null : profile.functional_classification })} disabled={!canEdit}>
              <SelectTrigger data-testid="paralympic-discipline"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="carrozzina">Carrozzina</SelectItem>
                <SelectItem value="non_vedenti">Non vedenti</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {showClassification && (
            <div>
              <Label>Classificazione funzionale (carrozzina)</Label>
              <Select value={profile.functional_classification || ""} onValueChange={(v) => setProfile({ ...profile, functional_classification: v })} disabled={!canEdit}>
                <SelectTrigger data-testid="paralympic-classification"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                  <SelectItem value="non_classificato">Non classificato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Stato classificazione</Label>
            <Select value={profile.classification_status || ""} onValueChange={(v) => setProfile({ ...profile, classification_status: v })} disabled={!canEdit}>
              <SelectTrigger data-testid="paralympic-status"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="provvisoria">Provvisoria</SelectItem>
                <SelectItem value="definitiva">Definitiva</SelectItem>
                <SelectItem value="non_classificato">Non classificato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Data classificazione</Label>
            <Input type="date" data-testid="paralympic-date" value={profile.classification_date || ""} disabled={!canEdit}
              onChange={(e) => setProfile({ ...profile, classification_date: e.target.value })} />
          </div>
          <div>
            <Label>Ente / commissione classificatrice</Label>
            <Input data-testid="paralympic-body" value={profile.classifying_body || ""} disabled={!canEdit}
              onChange={(e) => setProfile({ ...profile, classifying_body: e.target.value })} />
          </div>
          <div>
            <Label>Data scadenza (se prevista)</Label>
            <Input type="date" data-testid="paralympic-expiry" value={profile.expiry_date || ""} disabled={!canEdit}
              onChange={(e) => setProfile({ ...profile, expiry_date: e.target.value })} />
          </div>
          <div className="lg:col-span-2">
            <Label>Documento allegato (URL)</Label>
            <Input data-testid="paralympic-doc" value={profile.document_url || ""} disabled={!canEdit}
              placeholder="https://…" onChange={(e) => setProfile({ ...profile, document_url: e.target.value })} />
          </div>
          <div className="lg:col-span-2">
            <Label>Note riservate</Label>
            <Textarea rows={3} data-testid="paralympic-notes" value={profile.notes || ""} disabled={!canEdit}
              onChange={(e) => setProfile({ ...profile, notes: e.target.value })} />
          </div>
        </div>
      )}

      {canEdit && (
        <div className="pt-2">
          <Button data-testid="paralympic-save" onClick={save} disabled={loading} className="h-11 bg-black hover:bg-zinc-800">
            <Save className="w-4 h-4 mr-2" />{loading ? "Salvataggio…" : "Salva profilo paralimpico"}
          </Button>
        </div>
      )}

      <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-100">
        La classificazione paralimpica non sostituisce la categoria anagrafica. Le lettere A, B e C non sono categorie di età.
      </div>
    </div>
  );
}
